import { SearchType } from '@/app/(site)/search/types'
import { ctaSectionHeightWithGap } from '@/app/components/ad-grid/clickable-ad'
import {
    defaultMobileOffset,
    getCarouselOffset,
    getClickableAdOffset,
    getVideoOffset,
} from '@/app/components/ad-grid/spotlight-util'

import { IGif } from '@giphy/js-types'
import { getGifHeight } from '@giphy/js-util'
import { useMemo } from 'react'

type Props = {
    clickableAdGif: IGif | null
    spotlightGif: IGif | null
    carouselAd?: any
    includeSpotlight: boolean
    isMobile: boolean
    windowWidth: number
    type: SearchType
    clientRender: boolean
}

export function useAdOffsets({
    clickableAdGif,
    spotlightGif,
    carouselAd,
    includeSpotlight,
    isMobile,
    windowWidth,
    type,
    clientRender,
}: Props) {
    const clickableOffset = useMemo(() => {
        if (clickableAdGif) {
            return isMobile
                ? getGifHeight(clickableAdGif, windowWidth / 2) + ctaSectionHeightWithGap
                : getClickableAdOffset(clickableAdGif)
        }

        return 0
    }, [clickableAdGif, isMobile, windowWidth])

    const offset = useMemo(() => {
        // If there's a video spotlight use video offset, otherwise try spotlight gif
        // If spotlightGif is not provided, fall back to a carousel ad's first gif (if any)
        const carouselGif = carouselAd?.gif

        if (spotlightGif) {
            return spotlightGif.video ? getVideoOffset(spotlightGif) : getCarouselOffset(spotlightGif)
        }

        if (carouselGif) {
            return carouselGif.video ? getVideoOffset(carouselGif) : getCarouselOffset(carouselGif)
        }

        return 0
    }, [carouselAd?.gif, spotlightGif])

    const columnOffsets = useMemo(() => {
        if (includeSpotlight) {
            return [clickableOffset, 0, offset, offset]
        }

        if (clickableAdGif && type === 'gifs') {
            if (isMobile) {
                return clientRender ? [offset, 0] : [defaultMobileOffset, 0]
            }
            return [clickableOffset, 0, offset, offset]
        }

        return undefined
    }, [includeSpotlight, clickableAdGif, type, isMobile, clientRender, offset, clickableOffset])

    return { offset, columnOffsets }
}
